import React from "react";
import { NavLink, RouteComponentProps } from "react-router-dom";
import { SubCategory, searchQueryParams } from "../helpers/shared-interfaces";
import Table, { ColumnType } from "antd/lib/table";
import debounce from "lodash.debounce";
import queryString from "query-string";
import { convertSlaToDays, request, responseHandler } from "../helpers/utilities";
import * as Sentry from "@sentry/browser";
import { Button, Col, Switch, Row, message } from "antd";
import Card from "../components/Card";
import { PlusOutlined } from "@ant-design/icons";
import EntitledSection from "../components/EntitledSection";
import entitlements from "../config/entitlements";
import { GET_ADMIN_SUBCATEGORIES, UPDATE_SUBCATEGORY } from "../gql/categories";
import { extractPageFromQuery } from "../components/TableFilters";
import { DEBOUNCE_TIME } from "../helpers/constants";
import { PageToolbar } from "../components/PageToolbar";
import { generateDisplayErrorMessage } from "../helpers/utilities";



interface State {
  isLoading: boolean;
  subCategories: SubCategory[];
  page: number;
  pageSize: number;
  q: string;
  subcategoriesCount: number;
  autoCloseableLoading: Record<string, boolean>;
}

function getSubCategoriesColumn<T extends SubCategory>(
  toggleAutoCloseable: (record: SubCategory, checked: boolean) => void,
  autoCloseableLoading: Record<string, boolean>,
): ColumnType<T>[] {
  return [
    {
      title: "Department",
      dataIndex: "Department",
      key: "department",
      render(text: string, record: SubCategory): JSX.Element {
        return (
          <NavLink to={`/departments/${record?.category?.department?.id}`}>
            {record?.category?.department?.name}
          </NavLink>
        );
      },
    },
    {
      title: "Category",
      dataIndex: "category",
      key: "category",
      render(text: string, record: SubCategory): JSX.Element {
        return <span>{record?.category?.name}</span>;
      },
    },
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      render(text: string): JSX.Element {
        return <span>{text}</span>;
      },
    },
    {
      title: "SLA (Days)",
      dataIndex: "sla",
      key: "sla",
      render(text: string, record: SubCategory): JSX.Element {
        return <span>{convertSlaToDays(record.sla)}</span>;
      },
    },
    {
      title: "Calculate SLA Before",
      dataIndex: "slaCreateByEod",
      key: "slaCreateByEod",
      render(text: string, record: SubCategory): JSX.Element {
        return <span>{record.slaCreateByEod}</span>;
      },
    },
    {
      title: "Is Active?",
      dataIndex: "isActive",
      key: "isActive",
      align: "center",
      render(text: string, record: SubCategory): JSX.Element {
        return <span>{record.isActive?.toString()}</span>;
      },
    },
    {
      title: "Auto-Closeable",
      dataIndex: "autoCloseable",
      key: "autoCloseable",
      align: "center",
      render(text: string, record: SubCategory): JSX.Element {
        return (
          <span onClick={(e) => e.stopPropagation()}>
            <Switch
              checked={record.autoCloseable ?? false}
              loading={autoCloseableLoading[record.id] ?? false}
              onChange={(checked, e) => {
                e.stopPropagation();
                toggleAutoCloseable(record, checked);
              }}
            />
          </span>
        );
      },
    },
  ];
}

export default class SubCategories extends React.Component<
  RouteComponentProps,
  State
> {
  constructor(props: Readonly<RouteComponentProps>) {
    super(props);
    this.state = {
      isLoading: true,
      subCategories: [],
      subcategoriesCount: 0,
      page: 1,
      pageSize: 25,
      q: "",
      autoCloseableLoading: {},
    };

    this.nextPage = this.nextPage.bind(this);
    this.redirectToNewSubCategoryPage =
      this.redirectToNewSubCategoryPage.bind(this);
  }

  async componentDidMount(): Promise<void> {
    this.mergeWithLocalState();
    await this.triggerImmediateSearch();
  }

  redirectToNewSubCategoryPage = (): void => {
    this.props.history.push("/subCategories/new");
  };

  toggleAutoCloseable = async (record: SubCategory, checked: boolean): Promise<void> => {
    this.setState((prevState) => ({
      autoCloseableLoading: { ...prevState.autoCloseableLoading, [record.id]: true },
    }));

    try {
      const response = await request({
        query: UPDATE_SUBCATEGORY,
        variables: {
          subCategoryId: record.id,
          name: record.name,
          sla: record.sla,
          slaCreateByEod: record.slaCreateByEod,
          categoryId: record.category?.id,
          isActive: record.isActive,
          autoCloseable: checked,
        },
      });

      const { err, handled } = responseHandler(response, "updateSubCategory");

      if (handled) {
        message.error("An Error Occurred. Your request couldn't be processed.");
      } else if (err) {
        message.error(`Failed to update auto-closeable setting - ${generateDisplayErrorMessage(err)}`);
      } else {
        this.setState((prevState) => ({
          subCategories: prevState.subCategories.map((sc) =>
            sc.id === record.id ? { ...sc, autoCloseable: checked } : sc,
          ),
        }));
        message.success("Subcategory updated successfully");
      }
    } catch (error) {
      message.error(`Failed to update auto-closeable setting - ${generateDisplayErrorMessage(error)}`);
      Sentry.captureException(error);
    } finally {
      this.setState((prevState) => ({
        autoCloseableLoading: { ...prevState.autoCloseableLoading, [record.id]: false },
      }));
    }
  };

  async nextPage(page: number, pageSize: number): Promise<void> {
    this.setState(
      {
        isLoading: true,
        page,
        pageSize,
      },
      async () => {
        await this.triggerImmediateSearch();
      },
    );
  }

  handleSearchInput = (event: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({ q: event.target.value, page: 1 }, () =>
      this.triggerDelayedSearch(),
    );
  };

  triggerDelayedSearch = debounce(async (): Promise<void> => {
    return await this.triggerImmediateSearch();
  }, DEBOUNCE_TIME);

  triggerImmediateSearch = async (): Promise<void> => {
    const { q, page } = this.state;
    this.setState({ isLoading: true });

    try {
      const { data: { data } }: any = await request({
        query: GET_ADMIN_SUBCATEGORIES,
        variables: { page, q },
      });
      this.syncBrowserQueryString();
      this.setState({
        subCategories: data?.adminSubCategories?.results,
        subcategoriesCount: data?.adminSubCategories?.count,
      });
    } catch (error) {
      message.error(`Failed To Load SubCategories - ${generateDisplayErrorMessage(error)}`);
      Sentry.captureException(error);
    } finally {
      this.setState({ isLoading: false });
    }
  };

  extractQueryParamsFromUrl = (): searchQueryParams => {
    const parsed = queryString.parse(window.location.search);
    return JSON.parse(JSON.stringify(parsed)) as searchQueryParams;
  };

  mergeWithLocalState = async (): Promise<void> => {
    const parsed = this.extractQueryParamsFromUrl();
    let q = "";
    let page = 1;

    if (parsed.q) {
      q = parsed.q;
    }

    if (parsed.page) {
      page = extractPageFromQuery(parsed.page);
    }

    this.setState({
      q,
      page,
    });
  };

  syncBrowserQueryString = (): void => {
    const { q, page } = this.state;
    const queryStrings = [];
    if (q) {
      queryStrings.push(`q=${q}`);
    }
    queryStrings.push(`page=${page}`);

    const queryString = `?${queryStrings.join("&")}`;

    this.props.history.replace(queryString);
  };

  render(): JSX.Element | null {
    const {
      isLoading,
      q,
      subCategories,
      subcategoriesCount,
      page,
      pageSize,
      autoCloseableLoading,
    } = this.state;

    const resultsWithKeys: SubCategory[] = subCategories.map(
      (result: SubCategory): SubCategory => {
        return {
          ...result,
          id: result.id,
        };
      },
    );

    return (
        <Card>
          <div className="subCategories-container">
            <PageToolbar modelName="subcategories"
              searchValue={q}
              onSearchChange={this.handleSearchInput}
              searchPlaceholder="Search by name"
              primaryAction={
                <EntitledSection entitlement={entitlements.TransferRequests.create}>
                  <Button
                    type="primary"
                    onClick={this.redirectToNewSubCategoryPage}
                    data-testid="create-subCategory-button"
                  >
                    <PlusOutlined /> Create Subcategory
                  </Button>
                </EntitledSection>
              }
              totalCount={subcategoriesCount}
              currentDataCount={subCategories.length}
              page={page}
              pageSize={pageSize}
            />

            <Row>
              <Col span={24}>
                <Table
                  loading={isLoading}
                  scroll={{ x: 400 }}
                  columns={getSubCategoriesColumn(this.toggleAutoCloseable, autoCloseableLoading)}
                  dataSource={resultsWithKeys}
                  onRow={(record) => ({
                    onClick: () => {
                      this.props.history.push({
                        pathname: `/subCategories/${record.id}`,
                        state: { subCategories },
                      });
                    },
                    style: { cursor: 'pointer' }

                  })}
                  pagination={{
                    current: page,
                    defaultPageSize: pageSize,
                    total: subcategoriesCount,
                    onChange: this.nextPage,
                  }}
                />
              </Col>
            </Row>
          </div>
        </Card>
    );
  }
}
